<script setup lang="ts">
/**
 * One meal, for the family invited to it (SPEC §6.12, §8.6).
 *
 * Opened from a text message. The answer is the point of the screen, so it is
 * above everything else and everything else is short.
 */
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import ContributionList from '@/components/ContributionList.vue'
import PatientLoader from '@/components/PatientLoader.vue'
import RsvpCard from '@/components/RsvpCard.vue'
import {
  ApiError,
  endpoints,
  type AttendanceSummaryResponse,
  type FamilyMealResponse,
  type FamilyRsvpResponse,
} from '@/api'
import { asCount } from '@/utils/numbers'

const route = useRoute()

const eventId = computed(() => String(route.params.eventId))

const meal = ref<FamilyMealResponse | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)

const cancelled = computed(() => meal.value?.status === 'Cancelled')

/*
  Section 6 of SPEC §8.7.

  Fetched beside the meal rather than folded into it, because it is the one
  part of this screen that moves while somebody is looking at it — another
  family answering changes the number and nothing else — and because the
  meal payload is already the biggest thing the family surface loads.

  Failure is silent. A missing count is a smaller loss than an error box on a
  screen whose actual job, answering, still works.
*/
const attendance = ref<AttendanceSummaryResponse | null>(null)

const coming = computed(() => {
  const summary = attendance.value
  if (!summary) return null

  const people = asCount(summary.people)
  if (people === 0) return null

  const adults = asCount(summary.adults)
  const children = asCount(summary.children)

  return {
    headline: people === 1 ? '1 person coming' : `${people} people coming`,
    detail: [
      adults > 0 ? `${adults} ${adults === 1 ? 'adult' : 'adults'}` : null,
      children > 0 ? `${children} ${children === 1 ? 'kid' : 'kids'}` : null,
    ]
      .filter(Boolean)
      .join(' and '),
  }
})

const when = computed(() => {
  if (!meal.value) return ''

  const date = new Date(`${meal.value.localDate}T${meal.value.localTime}`)

  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
})

function describe(caught: unknown): string {
  if (caught instanceof ApiError && caught.status === 404) {
    return 'We cannot find that meal. It may have been called off, or it may not be one of yours.'
  }

  return (caught instanceof ApiError && caught.problem?.title) || 'That did not load.'
}

async function load() {
  loading.value = true
  error.value = null

  try {
    meal.value = await endpoints.familyMeal(eventId.value)
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }

  void loadAttendance()
}

/** Never fails the screen. See the note on `attendance`. */
async function loadAttendance(): Promise<void> {
  try {
    attendance.value = await endpoints.familyEventAttendance(eventId.value)
  } catch {
    attendance.value = null
  }
}

function adopt(rsvp: FamilyRsvpResponse) {
  if (meal.value) {
    meal.value = { ...meal.value, rsvp }
  }

  // The number this household just changed. Leaving the old one on screen
  // beside a freshly saved answer reads as the save not having worked.
  void loadAttendance()
}

void load()
</script>

<template>
  <div class="meal">
    <v-alert v-if="error" type="error" variant="tonal">{{ error }}</v-alert>

    <PatientLoader v-if="loading" label="this meal" @retry="load" />

    <template v-else-if="meal">
      <!--
        A cancelled meal stays here, said plainly and first. Somebody who saw
        it on Tuesday and cannot find it on Sunday concludes they
        misremembered, and turns up (SPEC §6.24.1).
      -->
      <v-alert v-if="cancelled" type="warning" variant="tonal" class="meal__cancelled">
        This meal has been cancelled. There is nothing to come to.
      </v-alert>

      <!--
        The order is SPEC §8.7's, and it is not arbitrary: header, answer, menu,
        what we are bringing, what is still needed, how many are coming, notes,
        where. It runs from the thing this screen exists for down to the things
        somebody only reads once. Notes and location used to sit above the menu,
        which put the least urgent content in front of the most.
      -->
      <header class="meal__head">
        <h1 class="meal__title" :class="{ 'meal__title--off': cancelled }">{{ meal.title }}</h1>
        <p class="meal__when">{{ when }} at {{ meal.localTime }}</p>
      </header>

      <RsvpCard
        v-if="!cancelled"
        :event-id="meal.id"
        :rsvp="meal.rsvp"
        :default-total="asCount(meal.defaultTotal)"
        :open="meal.isOpen"
        @saved="adopt"
      />

      <!-- Sections 3, 4 and 5. The menu, what this household claimed, and what
           still has nobody: one component, because they are three views of the
           same list and splitting them would let them disagree. -->
      <ContributionList
        v-if="!cancelled && (meal.menu?.length ?? 0) > 0"
        :menu="meal.menu"
        :open="meal.isOpen"
        @changed="load"
      />

      <!-- Section 6. Absent rather than zero when nobody has answered: "0
           people coming" on a meal announced an hour ago is discouraging and
           untrue. -->
      <section v-if="!cancelled && coming" class="meal__attendance" data-testid="attendance">
        <h2 class="meal__section">Who is coming</h2>
        <p class="meal__attendance-headline">{{ coming.headline }}</p>
        <p v-if="coming.detail" class="meal__attendance-detail">{{ coming.detail }}</p>
      </section>

      <!-- Section 7. -->
      <section v-if="meal.notes" class="meal__notes-section">
        <h2 class="meal__section">Notes</h2>
        <p class="meal__notes">{{ meal.notes }}</p>
      </section>

      <!-- Section 8. Its own section rather than a line under the title, so an
           address is something to look at on the day rather than furniture in
           the header. -->
      <section v-if="meal.locationName || meal.locationAddress" class="meal__where-section">
        <h2 class="meal__section">Where</h2>
        <p v-if="meal.locationName" class="meal__where">{{ meal.locationName }}</p>
        <p v-if="meal.locationAddress" class="meal__where-address">{{ meal.locationAddress }}</p>
      </section>
    </template>
  </div>
</template>

<style scoped>
.meal {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.meal__head {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.meal__title {
  font-size: 1.5rem;
  margin: 0;
}

.meal__title--off {
  text-decoration: line-through;
  color: rgb(var(--v-theme-on-surface-variant));
}

.meal__when {
  font-size: 1.05rem;
  margin: 0;
}

.meal__where,
.meal__section {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.35rem;
}

.meal__attendance-headline {
  margin: 0;
  font-size: 1.125rem;
}

.meal__attendance-detail {
  margin: 0.15rem 0 0;
  opacity: 0.75;
}

.meal__where-address {
  margin: 0.15rem 0 0;
  opacity: 0.75;
}

.meal__notes {
  color: rgb(var(--v-theme-on-surface-variant));
  margin: 0;
}

.meal__cancelled {
  margin: 0;
}
</style>
